<template>
  <Page :page-name="'Ledger List'">
    <div class="mt-4 w-full flex flex-col m-auto" style="height: 90vh">
      <Toolbar pt:root:class="mx-4 py-2">
        <template #start>
          <div class="flex flex-row gap-2">
            <SearchBar v-model:search-text="searchText" :show-refresh="false" @input="onFilterTextBoxChanged"></SearchBar>
            <ToolbarButtons label="Ledger" show-add @add="addLedgerClick"></ToolbarButtons>
          </div>
        </template>
      </Toolbar>
      <div style="width: 100%; flex: 1 1 auto">
        <ag-grid-vue
          theme="legacy"
          :rowData="ledgers"
          :columnDefs="colDefs"
          :defaultColDef="defaultColDefs"
          :autoSizeStrategy="{ type: 'fitGridWidth' }"
          :suppressCellFocus="true"
          class="mx-4 py-3 h-full"
          :class="darkVal ? 'ag-theme-quartz-dark' : 'ag-theme-quartz'"
          rowSelection="single"
          @row-selected="selectLedger"
          @row-data-updated="resize"
          @grid-size-changed="resize"
          @grid-ready="(event: GridReadyEvent) => gridApi = event.api"
        >
        </ag-grid-vue>
      </div>
    </div>
    <TSDialog v-model:visible="addLedger" label="Add Ledger" icon="plus" v-on:after-hide="hideAddLedger" size="w-[500px]">
      <template #content>
        <AddLedger @added="ledgerAdded" @cancelled="hideAddLedger" />
      </template>
    </TSDialog>
  </Page>
</template>

<script setup lang="ts">
import { AgGridVue } from "ag-grid-vue3";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import CustomBooleanIconCellRenderer from "@/components/cell-renderers/CustomBooleanIconCellRenderer.vue";
import { usePropsStore } from "@/stores/props";
import { CheckingAccountListSchema, type CheckingAccount } from "@/types/CheckingAccount";
import { useAuthStore } from "@/stores/auth";
import { UserPermissionsSchema, type UserPermissions } from "@/types/User";
import { getUserPermissions, permissionError } from "@/services/Permissions";
import type { GridApi, GridReadyEvent, GridSizeChangedEvent, RowDataUpdatedEvent, RowSelectedEvent, ValueGetterParams } from "ag-grid-community";

const router = useRouter();

const gridApi = ref<GridApi>();
const addLedger = ref(false);
const ledgers = ref<CheckingAccount[]>([]);

const searchText = ref("");

const propsStore = usePropsStore();

const userStore = useAuthStore();
const userPermissions = ref<UserPermissions>(UserPermissionsSchema.parse({}));

const darkVal = computed<boolean>(() => propsStore.darkMode);

onBeforeMount(async (): Promise<void> => {
  getUserPermissions(userStore.getUserInfo().userId).then((result: UserPermissions) => (userPermissions.value = result));

  await getLedgers();
});

const getLedgers = async (): Promise<void> => {
  await axios.get("/ledger").then((response) => {
    handleApiResponse(
      response,
      (data: any) => {
        parseData(CheckingAccountListSchema, data, ledgers);
      },
      (errors: ApiError[]) => {
        if (errors.length > 0) error("An Error Occurred", errors[0].message);
      }
    );
  });
};

const selectLedger = (event: RowSelectedEvent): void => {
  router.push({
    name: "ledger-dashboard",
    params: {
      id: event.data.checkingAccountId,
    },
  });
};

const addLedgerClick = (): void => {
  if (!userPermissions.value.addLedger) {
    permissionError();
    return;
  }
  addLedger.value = true;
};

const ledgerAdded = (): void => {
  hideAddLedger();
  getLedgers();
};

const hideAddLedger = (): void => {
  addLedger.value = false;
};

const onFilterTextBoxChanged = (): void => {
  gridApi.value?.setGridOption("quickFilterText", searchText.value);
};

const resize = (event: GridSizeChangedEvent | RowDataUpdatedEvent): void => {
  event.api.sizeColumnsToFit();
};

const defaultColDefs = {
  filter: true,
  floatingFilter: false,
  cellDataType: "text",
};

const colDefs = ref([
  {
    field: "description",
    headerName: "Name",
    width: 100,
  },
  {
    field: "bankName",
    headerName: "Bank Name",
    width: 100,
  },
  {
    field: "bankABANumber",
    headerName: "ABA #",
    width: 100,
  },
  {
    field: "accountNumber",
    headerName: "Account #",
    width: 100,
  },
  {
    headerName: "Group",
    width: 100,
    valueGetter: (params: ValueGetterParams) => params.data.userGroup.name,
  },
  {
    field: "active",
    headerName: "Active",
    cellDataType: "boolean",
    headerClass: "grid-align-center",
    cellClass: "text-center",
    width: 50,
    cellRenderer: CustomBooleanIconCellRenderer,
  },
  {
    field: "locked",
    headerName: "Locked",
    cellDataType: "boolean",
    headerClass: "grid-align-center",
    cellClass: "text-center",
    width: 50,
    cellRenderer: CustomBooleanIconCellRenderer,
  },
]);
</script>
